/**
 * refvalue-helpers.ts — Pure helper functions extracted from RefOrValue for testability.
 *
 * These functions are used by RefOrValue.tsx and can be independently tested.
 * No React or DOM dependencies.
 */

import type { ContentDefinition } from "@magic-md/core";
import { isUnitIdRef, normalizeUnitIdRef, splitScopedMemberPath } from "@magic-md/core";
import type { ScopeVariableColor } from "../types/index";
import { getOutputValueStyle } from "./theme-helpers";

/* ── Output Tree ── */

export interface OutputNode {
    key: string;
    path: string;
    value: unknown;
    type: "object" | "array" | "string" | "number" | "boolean" | "null" | "other";
    children?: OutputNode[];
}

export function buildOutputTree(
    value: unknown,
    basePath: string,
    maxDepth = 4,
    depth = 0,
): OutputNode[] {
    if (depth >= maxDepth || value === null || value === undefined) return [];

    if (Array.isArray(value)) {
        return value.slice(0, 20).map((item, i) => ({
            key: `[${i}]`,
            path: `${basePath}[${i}]`,
            value: item,
            type: getType(item),
            children:
                typeof item === "object" && item !== null
                    ? buildOutputTree(item, `${basePath}[${i}]`, maxDepth, depth + 1)
                    : undefined,
        }));
    }

    if (typeof value === "object") {
        return Object.entries(value as Record<string, unknown>)
            .filter(([k]) => !k.startsWith("__"))
            .map(([k, v]) => ({
                key: k,
                path: basePath ? `${basePath}.${k}` : k,
                value: v,
                type: getType(v),
                children:
                    typeof v === "object" && v !== null
                        ? buildOutputTree(
                            v,
                            basePath ? `${basePath}.${k}` : k,
                            maxDepth,
                            depth + 1,
                        )
                        : undefined,
            }));
    }

    return [];
}

function parseConfigPath(path: string): string[] {
    const matches = path.match(/[^.[\]]+|\[(\d+)\]/g);
    if (!matches) return [];
    return matches.map((segment) =>
        segment.startsWith("[") ? segment.slice(1, -1) : segment,
    );
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function isArrayIndex(segment: string): boolean {
    return /^\d+$/.test(segment);
}

export function getConfigValueAtPath(
    config: Record<string, unknown>,
    path: string,
): unknown {
    const segments = parseConfigPath(path);
    if (segments.length === 0) return undefined;

    let current: unknown = config;
    for (const segment of segments) {
        if (Array.isArray(current)) {
            current = current[Number.parseInt(segment, 10)];
            continue;
        }
        if (!isObjectLike(current)) return undefined;
        current = current[segment];
    }

    return current;
}

export function setConfigValueAtPath(
    config: Record<string, unknown>,
    path: string,
    value: unknown,
): Record<string, unknown> {
    const segments = parseConfigPath(path);
    if (segments.length === 0) return { ...config };

    const nextConfig: Record<string, unknown> = { ...config };
    let target: Record<string, unknown> | unknown[] = nextConfig;
    let source: unknown = config;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];
        const nextSegment = segments[index + 1];
        const sourceChild = Array.isArray(source)
            ? source[Number.parseInt(segment, 10)]
            : isObjectLike(source)
                ? source[segment]
                : undefined;

        const clonedChild = Array.isArray(sourceChild)
            ? [...sourceChild]
            : isObjectLike(sourceChild)
                ? { ...sourceChild }
                : isArrayIndex(nextSegment)
                    ? []
                    : {};

        if (Array.isArray(target)) {
            target[Number.parseInt(segment, 10)] = clonedChild;
        } else {
            target[segment] = clonedChild;
        }

        target = clonedChild;
        source = sourceChild;
    }

    const lastSegment = segments[segments.length - 1];
    if (Array.isArray(target)) {
        target[Number.parseInt(lastSegment, 10)] = value;
    } else {
        target[lastSegment] = value;
    }

    return nextConfig;
}

export function deleteConfigValueAtPath(
    config: Record<string, unknown>,
    path: string,
): Record<string, unknown> {
    const segments = parseConfigPath(path);
    if (segments.length === 0) return { ...config };

    const existingValue = getConfigValueAtPath(config, path);
    if (existingValue === undefined) return { ...config };

    const nextConfig: Record<string, unknown> = { ...config };
    let target: Record<string, unknown> | unknown[] = nextConfig;
    let source: unknown = config;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];
        const sourceChild = Array.isArray(source)
            ? source[Number.parseInt(segment, 10)]
            : isObjectLike(source)
                ? source[segment]
                : undefined;

        const clonedChild = Array.isArray(sourceChild)
            ? [...sourceChild]
            : isObjectLike(sourceChild)
                ? { ...sourceChild }
                : {};

        if (Array.isArray(target)) {
            target[Number.parseInt(segment, 10)] = clonedChild;
        } else {
            target[segment] = clonedChild;
        }

        target = clonedChild;
        source = sourceChild;
    }

    const lastSegment = segments[segments.length - 1];
    if (Array.isArray(target)) {
        target.splice(Number.parseInt(lastSegment, 10), 1);
    } else {
        delete target[lastSegment];
    }

    return nextConfig;
}

export function getType(v: unknown): OutputNode["type"] {
    if (v === null) return "null";
    if (Array.isArray(v)) return "array";
    const t = typeof v;
    if (t === "object") return "object";
    if (t === "string") return "string";
    if (t === "number") return "number";
    if (t === "boolean") return "boolean";
    return "other";
}

export function typeColor(type: OutputNode["type"]): string {
    return getOutputValueStyle(type).color ?? "var(--color-muted-foreground)";
}

export function typeLabel(type: OutputNode["type"], value: unknown): string {
    switch (type) {
        case "array":
            return `Array(${Array.isArray(value) ? value.length : "?"})`;
        case "object":
            return `Object(${value ? Object.keys(value as Record<string, unknown>).filter((k) => !k.startsWith("__")).length : 0})`;
        case "string":
            return `"${String(value).slice(0, 30)}${String(value).length > 30 ? "…" : ""}"`;
        case "number":
        case "boolean":
            return String(value);
        case "null":
            return "null";
        default:
            return String(value);
    }
}

/* ── Ref Detection & Classification ── */

export function isRefValue(value: unknown): value is string {
    return (
        typeof value === "string" &&
        value.startsWith("$ref{") &&
        value.endsWith("}")
    );
}

export function isAnyRefValue(value: unknown): boolean {
    return (
        isRefValue(value) ||
        (typeof value === "string" && value.startsWith("$var{") && value.endsWith("}"))
    );
}

export function formatRefDisplay(refStr: string): string {
    if (refStr.startsWith("$var{")) return refStr.slice(5, -1);
    return refStr.slice(5, -1);
}

export function extractInputParamNameFromRef(refStr: unknown): string | undefined {
    if (!isRefValue(refStr)) return undefined;
    const inner = (refStr as string).slice(5, -1);
    return inner.startsWith("input/") ? inner.slice("input/".length) : undefined;
}

export function formatInputParamRef(paramName: string): string {
    return `$ref{input/${paramName.trim()}}`;
}

export function getRefColor(refStr: string): ScopeVariableColor {
    if (refStr.startsWith("$var{")) return "amber";
    if (isRefValue(refStr)) {
        const inner = refStr.slice(5, -1);
        if (inner.startsWith("$")) return "purple";
        if (inner.startsWith("input/")) return "blue"; // ::use template param
        if (inner.startsWith("unit/")) return "purple"; // unit identity ref
        if (inner.startsWith("assets/") || /^[\w-]+\/assets\//.test(inner)) return "green"; // document/import asset
        if (inner.includes("/")) return "green";       // import ref (ns/id)
    }
    return "amber";
}

export function getRefColorWithContext(
    refStr: string,
    contentDefs: Map<string, ContentDefinition>,
): ScopeVariableColor {
    const defId = extractDefIdFromRef(refStr);
    if (defId) {
        if (contentDefs.has(defId)) return "blue";
        const scoped = splitScopedMemberPath(
            defId,
            (id) => contentDefs.has(id),
            (id) => contentDefs.has(id),
        );
        if (scoped) return "blue";
    }
    return getRefColor(refStr);
}

export function getRefPrefix(refStr: string): string {
    if (refStr.startsWith("$var{")) return "$var";
    return "$ref";
}

export function extractDefIdFromRef(refStr: unknown): string | undefined {
    if (!isRefValue(refStr)) return undefined;
    if (isUnitIdRef(refStr)) return normalizeUnitIdRef(refStr);
    const inner = (refStr as string).slice(5, -1);
    const dotIdx = inner.indexOf(".");
    return dotIdx >= 0 ? inner.slice(0, dotIdx) : inner;
}

export function extractContentIdFromRef(
    refStr: unknown,
    contentDefs: Map<string, ContentDefinition>,
): string | undefined {
    const defId = extractDefIdFromRef(refStr);
    if (!defId) return undefined;
    if (contentDefs.has(defId)) return defId;
    const scoped = splitScopedMemberPath(
        defId,
        (id) => contentDefs.has(id),
        (id) => contentDefs.has(id),
    );
    return scoped?.rootId;
}
